import { useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import { useUpdateUser } from '../hooks/use-update-user';
import {
  detectDocumentType,
  formatCEP,
  formatDocumentInput,
  formatPhone,
  isBrazilianCountry,
  isValidCEP,
  isValidDocument,
  isValidPhone,
  normalizePhone,
  normalizeZipCode,
  onlyDigits,
} from '@/shared/utils/br-fields';
import { DocumentValidityBadge } from '@/shared/components/document-validity-badge';
import type { Gender, UserDetail } from '../../domain/entities/user.entity';

interface EditUserModalProps {
  user: UserDetail;
  open: boolean;
  onClose: () => void;
}

export function EditUserModal({ user, open, onClose }: EditUserModalProps) {
  const [name, setName] = useState(user.name);
  const [gender, setGender] = useState<string>(user.gender ?? '');
  // Mantido mascarado no state (é o que aparece no input); só vira dígitos no
  // submit. `doc` aceita CPF ou CNPJ — a máscara se adapta pela quantidade de
  // dígitos (nunca trunca um CNPJ que já esteja salvo).
  const [doc, setDoc] = useState(formatDocumentInput(user.doc ?? ''));
  const [docTouched, setDocTouched] = useState(false);
  const [phone, setPhone] = useState(formatPhone(user.phone ?? ''));
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [street, setStreet] = useState(user.address?.street ?? '');
  const [city, setCity] = useState(user.address?.city ?? '');
  const [state, setState] = useState(user.address?.state ?? '');
  const [country, setCountry] = useState(user.address?.country ?? '');
  const [zipCode, setZipCode] = useState(
    formatZipCodeIfBR(user.address?.zipCode ?? '', user.address?.country ?? ''),
  );
  const [zipCodeTouched, setZipCodeTouched] = useState(false);
  const [number, setNumber] = useState(user.address?.number ?? '');
  const [neighborhood, setNeighborhood] = useState(
    user.address?.neighborhood ?? '',
  );
  const [complement, setComplement] = useState(user.address?.complement ?? '');

  const { mutate, isPending, error } = useUpdateUser(user.userId);

  // Documento e telefone são opcionais — só validamos o que foi preenchido.
  // Tipo (CPF/CNPJ) inferido pela quantidade de dígitos — o campo aceita os
  // dois, nunca só CPF (aluno pode estar cadastrado com CNPJ da empresa).
  const docType = detectDocumentType(doc);
  const docTypeLabel = docType === 'cnpj' ? 'CNPJ' : 'CPF';
  const docError =
    doc.trim() && !isValidDocument(doc)
      ? `${docTypeLabel} inválido.`
      : undefined;
  const phoneError =
    phone.trim() && !isValidPhone(phone) ? 'Telefone inválido.' : undefined;
  // Fora do Brasil o código postal tem formato próprio — não há o que validar.
  const brAddress = isBrazilianCountry(country);
  const zipCodeError =
    brAddress && zipCode.trim() && !isValidCEP(zipCode)
      ? 'CEP inválido.'
      : undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (docError || phoneError || zipCodeError) {
      setDocTouched(true);
      setPhoneTouched(true);
      setZipCodeTouched(true);
      return;
    }
    // Endereço é tudo-ou-nada (o VO de domínio exige os 5 campos).
    const addressFilled = [street, city, state, country, zipCode].every((v) =>
      v.trim(),
    );
    mutate(
      {
        name: name.trim() || undefined,
        gender: (gender || null) as Gender | null,
        // Persistido sem máscara: o formato é decisão de exibição.
        doc: onlyDigits(doc) || null,
        phone: normalizePhone(phone) || null,
        address: addressFilled
          ? {
              street,
              city,
              state,
              country,
              zipCode: normalizeZipCode(zipCode),
              number: number.trim() || null,
              neighborhood: neighborhood.trim() || null,
              complement: complement.trim() || null,
            }
          : undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Editar usuário"
      description="Atualize nome, documento, telefone, gênero e endereço. (Endereço só é salvo com os 5 campos preenchidos.)"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-(--color-text-secondary) text-sm font-medium">
                Documento ({docTypeLabel})
              </span>
              <DocumentValidityBadge value={doc} />
            </div>
            <Input
              value={doc}
              inputMode="numeric"
              placeholder="000.000.000-00"
              onChange={(e) => setDoc(formatDocumentInput(e.target.value))}
              // O erro só aparece depois de sair do campo (ou tentar salvar),
              // senão acusaria "inválido" já no primeiro dígito digitado.
              onBlur={() => setDocTouched(true)}
              error={docTouched ? docError : undefined}
            />
          </div>
          <Input
            label="Telefone"
            value={phone}
            inputMode="tel"
            placeholder="(00) 00000-0000"
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            onBlur={() => setPhoneTouched(true)}
            error={phoneTouched ? phoneError : undefined}
            helperText="Estrangeiro: comece com + e o código do país."
          />
        </div>
        <Select
          label="Gênero"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          options={[
            { value: '', label: 'Não informado' },
            { value: 'MALE', label: 'Masculino' },
            { value: 'FEMALE', label: 'Feminino' },
            { value: 'OTHER', label: 'Outro' },
          ]}
        />
        <fieldset className="space-y-3">
          <legend className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
            Endereço
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Logradouro"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
            <Input
              label="Número"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bairro"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
            />
            <Input
              label="Complemento"
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Cidade"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <Input
              label="Estado"
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
            <Input
              label="País"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
            <Input
              label="CEP"
              value={zipCode}
              inputMode={brAddress ? 'numeric' : 'text'}
              placeholder={brAddress ? '00000-000' : undefined}
              onChange={(e) =>
                setZipCode(formatZipCodeIfBR(e.target.value, country))
              }
              onBlur={() => setZipCodeTouched(true)}
              error={zipCodeTouched ? zipCodeError : undefined}
            />
          </div>
        </fieldset>
        {error && (
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Falha ao salvar'}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * A máscara de CEP só faz sentido em endereço brasileiro; num endereço
 * estrangeiro ela destruiria o código postal local (`SW1A 1AA`).
 */
function formatZipCodeIfBR(value: string, country: string): string {
  return isBrazilianCountry(country) ? formatCEP(value) : value;
}
